import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TranslationLoaderHelper {
  private loaded = new Map<string, Set<string>>();

  private loadingCount = 0;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public readonly loading$ = this.loadingSubject.asObservable();

  private readonly debugEnabled = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('DEBUG_JSON_LOADING') === 'true';

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {
    // When the active language changes, reload modules that were previously loaded
    // under a different language. This handles eagerly-loaded modules (e.g. SharedModule,
    // CoreModule) whose mergeModuleTranslations() fired at app boot with the default lang
    // before auth resolved and switched to the user's actual locale.
    this.translate.onLangChange
      .pipe(map(e => e.lang))
      .subscribe(newLang => {
        const alreadyLoadedModules = new Set<string>();
        this.loaded.forEach((modules, lang) => {
          if (lang !== newLang) {
            modules.forEach(moduleName => alreadyLoadedModules.add(moduleName));
          }
        });
        alreadyLoadedModules.forEach(moduleName => {
          void this.mergeModuleTranslations(moduleName)
            .catch(error => {
              if (this.debugEnabled) {
                console.error(`Failed to reload translations for module "${moduleName}"`, error);
              }
            });
        });
      });
  }

  private async waitForLanguage(): Promise<string> {
    const lang = this.translate.currentLang || this.translate.defaultLang;

    if (lang) {
      return lang;
    }

    return firstValueFrom(
      this.translate.onLangChange.pipe(
        map(e => e.lang),
        take(1)
      )
    );
  }

  /**
   * Set the loading state for a specific module.
   * This method will debug log the loading state changes via the console if the 'DEBUG_JSON_LOADING' flag is set in sessionStorage.
   * 
   * @param start Determines if we are starting or ending a loading process. True for starting, false for ending.
   * @param moduleName Optional name of the module for logging purposes. If not provided, 'unknown module' will be used in logs.
   * @param lang Optional language code for logging purposes. If not provided, 'unknown language' will be used in logs.
   */
  private setLoading(start: boolean, moduleName?: string, lang?: string) {

    if (start) {
      this.loadingCount++;
    } else {
      this.loadingCount--;
    }

    if (this.loadingCount < 0) {
      if (this.debugEnabled) {
        console.warn('[i18n loader] Loading counter went below zero. Resetting.');
      }
      this.loadingCount = 0;
    }

    const isLoading = this.loadingCount > 0;

    if (this.debugEnabled) {
      const action = start ? 'START' : 'END';

      console.log(
        `[i18n module loader] ${action} ${moduleName ?? 'unknown module'} | ${lang ?? 'unknown language'} | ` +
        `activeLoads=${this.loadingCount} | globalLoading=${isLoading}`
      );
    }

    this.loadingSubject.next(isLoading);
  }

  async mergeModuleTranslations(moduleName: string): Promise<void> {
    const lang = await this.waitForLanguage();

    let loadedForLang = this.loaded.get(lang);
    if (!loadedForLang) {
      loadedForLang = new Set<string>();
      this.loaded.set(lang, loadedForLang);
    }

    if (loadedForLang.has(moduleName)) {
      return;
    }

    loadedForLang.add(moduleName);

    const path = `assets/i18n/${moduleName}/${lang}.json`;

    this.setLoading(true, moduleName, lang);

    try {
      const translations = await firstValueFrom(
        this.http.get<Record<string, any>>(path)
      );
      this.translate.setTranslation(lang, translations, true);
    } catch (err) {
      loadedForLang.delete(moduleName);
      console.warn(`[i18n] Could not load ${path}`, err);
    } finally {
      this.setLoading(false, moduleName, lang);
    }
  }
}
